import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  Lightbulb,
  MapPin,
  Plus,
  Send,
  Sparkles,
  Trash2,
  UserRound,
  Users,
} from "lucide-react";
import type { Language, PikbMember, PikbSubmission } from "../types";
import {
  getPikbCategoryLabel,
  getPikbZoneLabel,
  getWordCount,
  pikbCategories,
  pikbCopy,
  pikbDefaultSubmission,
  pikbEventInfo,
  pikbJoiningZones,
  pikbMinimumSummaryWords,
  pikbRules,
  pikbSteps,
  pikbZones,
} from "../data/pikb";
import { ProgressMeter } from "../components/ProgressMeter";
import { formatServiceError, getErrorMessage } from "../lib/errors";
import { getPikbSessionKey, resetPikbSessionKey } from "../lib/storage";
import { submitPikbSurvey, trackPikbEvent } from "../lib/supabase";

type Errors = Record<string, string>;

const language: Language = "ms";

const pageMotion = {
  initial: { opacity: 0, y: 16, scale: 0.985 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -16, scale: 0.985 },
  transition: { duration: 0.24, ease: [0.22, 1, 0.36, 1] as const },
};

const emptyMember: PikbMember = {
  name: "",
  ic_number: "",
  phone: "",
};

function formatSubmitError(error: unknown) {
  const message = getErrorMessage(error);

  if (
    /pikb_submissions|pikb_events|relation .* does not exist/i.test(message)
  ) {
    return "Sistem belum siap sepenuhnya. Jadual pangkalan data PIKB belum diwujudkan.";
  }

  return formatServiceError(
    error,
    "Penyertaan tidak dapat dihantar. Sila cuba lagi.",
  );
}

function isValidIcNumber(value: string) {
  return /^\d{12}$/.test(value.trim());
}

function validateStep(stepIndex: number, submission: PikbSubmission) {
  const errors: Errors = {};
  const required = pikbCopy.required.ms;

  if (stepIndex === 1 && !submission.zone) {
    errors.zone = required;
  }

  if (stepIndex === 2 && !submission.category) {
    errors.category = required;
  }

  if (stepIndex === 3) {
    if (!submission.leader_name.trim()) errors.leader_name = required;
    if (!submission.leader_ic.trim()) {
      errors.leader_ic = required;
    } else if (!isValidIcNumber(submission.leader_ic)) {
      errors.leader_ic = "No Kad Pengenalan mesti 12 digit nombor.";
    }
    if (!submission.leader_phone.trim()) errors.leader_phone = required;
    if (!submission.leader_address.trim()) errors.leader_address = required;

    submission.members.forEach((member, index) => {
      if (!member.name.trim()) errors[`members.${index}.name`] = required;
      if (!member.ic_number.trim()) {
        errors[`members.${index}.ic_number`] = required;
      } else if (!isValidIcNumber(member.ic_number)) {
        errors[`members.${index}.ic_number`] =
          "No Kad Pengenalan mesti 12 digit nombor.";
      }
      if (!member.phone.trim()) errors[`members.${index}.phone`] = required;
    });
  }

  if (stepIndex === 4) {
    if (!submission.innovation_title.trim()) {
      errors.innovation_title = required;
    }

    if (!submission.innovation_summary.trim()) {
      errors.innovation_summary = required;
    } else if (getWordCount(submission.innovation_summary) < pikbMinimumSummaryWords) {
      errors.innovation_summary = pikbCopy.minimumWords.ms;
    }
  }

  return errors;
}

function validateAll(submission: PikbSubmission) {
  return [1, 2, 3, 4].reduce(
    (allErrors, index) => ({
      ...allErrors,
      ...validateStep(index, submission),
    }),
    {} as Errors,
  );
}

export default function PikbSurveyPage() {
  const [submission, setSubmission] =
    useState<PikbSubmission>(pikbDefaultSubmission);
  const [stepIndex, setStepIndex] = useState(0);
  const [errors, setErrors] = useState<Errors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isComplete, setIsComplete] = useState(false);
  const [sessionKey] = useState(() => getPikbSessionKey());
  const startedRef = useRef(false);
  const activeStep = pikbSteps[stepIndex];
  const progress = useMemo(
    () => Math.round((stepIndex / (pikbSteps.length - 1)) * 100),
    [stepIndex],
  );

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void trackPikbEvent({
      session_key: sessionKey,
      event_type: "started",
      step_index: 0,
      language,
      metadata: { survey: "pikb" },
    });
  }, [sessionKey]);

  useEffect(() => {
    if (isComplete) return;
    void trackPikbEvent({
      session_key: sessionKey,
      event_type: "step_viewed",
      step_index: stepIndex,
      language,
      metadata: { survey: "pikb" },
    });
  }, [isComplete, sessionKey, stepIndex]);

  const updateField = (
    field: Exclude<keyof PikbSubmission, "members">,
    value: string,
  ) => {
    const nextValue =
      field === "leader_ic" ? value.replace(/\D/g, "").slice(0, 12) : value;
    setSubmission((current) => ({ ...current, [field]: nextValue }));
    setErrors((current) => ({ ...current, [field]: "" }));
  };

  const updateMember = (
    index: number,
    field: keyof PikbMember,
    value: string,
  ) => {
    const nextValue =
      field === "ic_number" ? value.replace(/\D/g, "").slice(0, 12) : value;
    setSubmission((current) => ({
      ...current,
      members: current.members.map((member, memberIndex) =>
        memberIndex === index ? { ...member, [field]: nextValue } : member,
      ),
    }));
    setErrors((current) => ({
      ...current,
      [`members.${index}.${field}`]: "",
    }));
  };

  const addMember = () => {
    setSubmission((current) => ({
      ...current,
      members: [...current.members, { ...emptyMember }],
    }));
  };

  const removeMember = (index: number) => {
    setSubmission((current) => ({
      ...current,
      members: current.members.filter((_, memberIndex) => memberIndex !== index),
    }));
  };

  const goNext = () => {
    const stepErrors = validateStep(stepIndex, submission);
    setErrors(stepErrors);
    if (Object.values(stepErrors).some(Boolean)) return;
    setStepIndex((current) => Math.min(current + 1, pikbSteps.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setStepIndex((current) => Math.max(current - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    const allErrors = validateAll(submission);
    setErrors(allErrors);
    if (Object.values(allErrors).some(Boolean)) {
      if (allErrors.zone) setStepIndex(1);
      else if (allErrors.category) setStepIndex(2);
      else if (
        allErrors.leader_name ||
        allErrors.leader_ic ||
        allErrors.leader_phone ||
        allErrors.leader_address ||
        Object.keys(allErrors).some((key) => key.startsWith("members."))
      ) {
        setStepIndex(3);
      } else {
        setStepIndex(4);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    try {
      await submitPikbSurvey(submission, language);
      await trackPikbEvent({
        session_key: sessionKey,
        event_type: "completed",
        step_index: pikbSteps.length - 1,
        language,
        metadata: { survey: "pikb", zone: submission.zone },
      });
      resetPikbSessionKey();
      setIsComplete(true);
    } catch (error) {
      setSubmitError(formatSubmitError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="pikb-public-page">
      <section className="pikb-wizard-shell" aria-live="polite">
        {isComplete ? (
          <SuccessStep />
        ) : (
          <>
            <ProgressMeter
              className="pikb-page-progress"
              label={activeStep.eyebrow.ms}
              current={stepIndex + 1}
              total={pikbSteps.length}
              value={progress}
            />

            <AnimatePresence mode="wait">
              <motion.section
                key={activeStep.id}
                {...pageMotion}
                className="wizard-step"
              >
                <header className="dialog-header pikb-page-header">
                  <span className="dialog-icon">
                    {activeStep.id === "zone" ? (
                      <MapPin size={28} />
                    ) : activeStep.id === "leader" ? (
                      <Users size={28} />
                    ) : activeStep.id === "innovation" ? (
                      <Lightbulb size={28} />
                    ) : (
                      <Sparkles size={28} />
                    )}
                  </span>
                  <h1>{activeStep.title.ms}</h1>
                  <p>{activeStep.description.ms}</p>
                </header>

                {activeStep.id === "intro" ? (
                  <IntroStep />
                ) : activeStep.id === "zone" ? (
                  <ZoneStep
                    value={submission.zone}
                    error={errors.zone}
                    onChange={(value) => updateField("zone", value)}
                  />
                ) : activeStep.id === "category" ? (
                  <CategoryStep
                    value={submission.category}
                    error={errors.category}
                    onChange={(value) => updateField("category", value)}
                  />
                ) : activeStep.id === "leader" ? (
                  <LeaderStep
                    submission={submission}
                    errors={errors}
                    updateField={updateField}
                    updateMember={updateMember}
                    addMember={addMember}
                    removeMember={removeMember}
                  />
                ) : activeStep.id === "innovation" ? (
                  <InnovationStep
                    submission={submission}
                    errors={errors}
                    updateField={updateField}
                  />
                ) : (
                  <ReviewStep submission={submission} />
                )}
              </motion.section>
            </AnimatePresence>

            {submitError ? <div className="submit-error">{submitError}</div> : null}

            <div className="pikb-page-actions">
              <button
                className="secondary-button"
                type="button"
                onClick={goBack}
                disabled={stepIndex === 0 || isSubmitting}
              >
                <ArrowLeft size={18} />
                {pikbCopy.back.ms}
              </button>

              {stepIndex === pikbSteps.length - 1 ? (
                <button
                  className="primary-button"
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? pikbCopy.submitting.ms : pikbCopy.submit.ms}
                  <Send size={18} />
                </button>
              ) : (
                <button className="primary-button" type="button" onClick={goNext}>
                  {stepIndex === 0 ? pikbCopy.continue.ms : pikbCopy.next.ms}
                  <ArrowRight size={18} />
                </button>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}

function IntroStep() {
  return (
    <div className="intro-step">
      <div className="pikb-brand-row" aria-label="Anjuran PIKB">
        <span className="pikb-logo-mark">PIKB 2026</span>
        <div>
          <span>Anjuran</span>
          <strong>Institut Kajian Pembangunan (Sabah)</strong>
          <strong>Universiti Malaysia Sabah</strong>
        </div>
      </div>
      <p className="intro-purpose">
        Pertandingan ini memberi fokus kepada pembangunan inovasi berasaskan
        kearifan lokal, pengetahuan tradisional serta sumber tempatan masyarakat
        Borneo.
      </p>
      <div className="event-info-grid">
        {pikbEventInfo.map((item) => (
          <div key={item.label.ms}>
            <span>{item.label.ms}</span>
            <strong>{item.value.ms}</strong>
          </div>
        ))}
      </div>
      <div className="zone-schedule-list" aria-label="Jadual semua zon PIKB">
        {pikbZones.map((zone) => (
          <article
            className={zone.finaleOnly ? "zone-schedule-card is-finale" : "zone-schedule-card"}
            key={zone.value}
          >
            <span>{zone.label.ms}</span>
            <strong>
              {zone.venue.ms} | {zone.date.ms}
            </strong>
          </article>
        ))}
      </div>
      <ul className="rule-list">
        {pikbRules.ms.map((rule) => (
          <li key={rule}>{rule}</li>
        ))}
      </ul>
    </div>
  );
}

function ZoneStep({
  value,
  error,
  onChange,
}: {
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <div className="zone-card-grid" role="group" aria-label="Pilihan zon">
        {pikbJoiningZones.map((zone) => (
          <button
            className={`zone-option-card ${
              value === zone.value ? "is-selected" : ""
            }`}
            key={zone.value}
            type="button"
            aria-pressed={value === zone.value}
            onClick={() => onChange(zone.value)}
          >
            <span>
              <MapPin size={18} />
              {zone.label.ms}
            </span>
            <strong>{zone.venue.ms}</strong>
            <small>
              <CalendarDays size={16} />
              {zone.date.ms}
            </small>
          </button>
        ))}
      </div>
      {error ? <p className="field-error centered-error">{error}</p> : null}
    </div>
  );
}

function CategoryStep({
  value,
  error,
  onChange,
}: {
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <div className="selectable-card-grid" role="group" aria-label="Kategori pertandingan">
        {pikbCategories.map((category) => (
          <button
            className={`selectable-card ${
              value === category.value ? "is-selected" : ""
            }`}
            key={category.value}
            type="button"
            aria-pressed={value === category.value}
            onClick={() => onChange(category.value)}
          >
            <Lightbulb size={28} />
            <span>{category.label.ms}</span>
            <small>{category.description.ms}</small>
          </button>
        ))}
      </div>
      {error ? <p className="field-error centered-error">{error}</p> : null}
    </div>
  );
}

function LeaderStep({
  submission,
  errors,
  updateField,
  updateMember,
  addMember,
  removeMember,
}: {
  submission: PikbSubmission;
  errors: Errors;
  updateField: (
    field: Exclude<keyof PikbSubmission, "members">,
    value: string,
  ) => void;
  updateMember: (index: number, field: keyof PikbMember, value: string) => void;
  addMember: () => void;
  removeMember: (index: number) => void;
}) {
  return (
    <div className="pikb-form-stack">
      <div className="form-grid">
        <TextField
          label="Nama ketua kumpulan"
          value={submission.leader_name}
          error={errors.leader_name}
          onChange={(value) => updateField("leader_name", value)}
        />
        <TextField
          label="No Kad Pengenalan"
          value={submission.leader_ic}
          error={errors.leader_ic}
          inputMode="numeric"
          maxLength={12}
          onChange={(value) => updateField("leader_ic", value)}
        />
        <TextField
          label="No Tel"
          value={submission.leader_phone}
          error={errors.leader_phone}
          onChange={(value) => updateField("leader_phone", value)}
        />
        <TextField
          className="span-full"
          label="Alamat"
          value={submission.leader_address}
          error={errors.leader_address}
          onChange={(value) => updateField("leader_address", value)}
        />
      </div>

      <div className="members-header">
        <div>
          <strong>Nama Ahli</strong>
          <span>Tambah ahli kumpulan jika ada.</span>
        </div>
        <button className="secondary-button" type="button" onClick={addMember}>
          <Plus size={18} />
          {pikbCopy.addMember.ms}
        </button>
      </div>

      {submission.members.length ? (
        <div className="member-list">
          {submission.members.map((member, index) => (
            <div className="member-card" key={index}>
              <div className="member-card-header">
                <span>
                  <UserRound size={17} />
                  Ahli {index + 1}
                </span>
                <button
                  className="icon-only danger-button"
                  type="button"
                  onClick={() => removeMember(index)}
                  aria-label={pikbCopy.removeMember.ms}
                  title={pikbCopy.removeMember.ms}
                >
                  <Trash2 size={17} />
                </button>
              </div>
              <div className="form-grid">
                <TextField
                  label="Nama ahli"
                  value={member.name}
                  error={errors[`members.${index}.name`]}
                  onChange={(value) => updateMember(index, "name", value)}
                />
                <TextField
                  label="No Kad Pengenalan"
                  value={member.ic_number}
                  error={errors[`members.${index}.ic_number`]}
                  inputMode="numeric"
                  maxLength={12}
                  onChange={(value) => updateMember(index, "ic_number", value)}
                />
                <TextField
                  label="No Tel"
                  value={member.phone}
                  error={errors[`members.${index}.phone`]}
                  onChange={(value) => updateMember(index, "phone", value)}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="empty-members">{pikbCopy.noMembers.ms}</p>
      )}
    </div>
  );
}

function InnovationStep({
  submission,
  errors,
  updateField,
}: {
  submission: PikbSubmission;
  errors: Errors;
  updateField: (
    field: Exclude<keyof PikbSubmission, "members">,
    value: string,
  ) => void;
}) {
  const wordCount = getWordCount(submission.innovation_summary);

  return (
    <div className="pikb-form-stack">
      <TextField
        label="Tajuk Inovasi"
        value={submission.innovation_title}
        error={errors.innovation_title}
        onChange={(value) => updateField("innovation_title", value)}
      />
      <label className="form-field">
        <span>Penjelasan ringkas mengenai inovasi</span>
        <textarea
          value={submission.innovation_summary}
          onChange={(event) =>
            updateField("innovation_summary", event.target.value)
          }
          rows={7}
          placeholder="Apakah keunikan atau keistimewaan inovasi anda?"
          aria-invalid={Boolean(errors.innovation_summary)}
        />
        <span
          className={`word-count inline-count ${
            wordCount < pikbMinimumSummaryWords ? "is-low" : ""
          }`}
        >
          {wordCount} {pikbCopy.words.ms}
        </span>
        {errors.innovation_summary ? (
          <p className="field-error">{errors.innovation_summary}</p>
        ) : null}
      </label>
    </div>
  );
}

function ReviewStep({ submission }: { submission: PikbSubmission }) {
  const members =
    submission.members.length > 0
      ? submission.members
          .map((member, index) => `${index + 1}. ${member.name}`)
          .join(", ")
      : pikbCopy.noMembers.ms;

  return (
    <div className="review-step">
      <p className="review-copy">{pikbCopy.review.ms}</p>
      <div className="review-grid">
        <ReviewItem
          label="Zon"
          value={getPikbZoneLabel(submission.zone, language)}
        />
        <ReviewItem
          label="Kategori"
          value={getPikbCategoryLabel(submission.category, language)}
        />
        <ReviewItem label="Ketua kumpulan" value={submission.leader_name} />
        <ReviewItem label="No Kad Pengenalan" value={submission.leader_ic} />
        <ReviewItem label="No Tel" value={submission.leader_phone} />
        <ReviewItem label="Alamat" value={submission.leader_address} />
        <ReviewItem label="Ahli" value={members} />
        <ReviewItem label="Tajuk Inovasi" value={submission.innovation_title} />
        <ReviewItem
          label="Penjelasan ringkas"
          value={submission.innovation_summary}
        />
      </div>
    </div>
  );
}

function SuccessStep() {
  return (
    <div className="modal-success pikb-page-success">
      <span className="success-badge">
        <Check size={38} />
      </span>
      <h1>{pikbCopy.thankYou.ms}</h1>
      <p>{pikbCopy.thankYouDetail.ms}</p>
    </div>
  );
}

function TextField({
  label,
  value,
  error,
  className = "",
  inputMode,
  maxLength,
  onChange,
}: {
  label: string;
  value: string;
  error?: string;
  className?: string;
  inputMode?: "text" | "numeric" | "decimal" | "tel" | "email" | "url";
  maxLength?: number;
  onChange: (value: string) => void;
}) {
  return (
    <label className={`form-field ${className}`}>
      <span>{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete="off"
        inputMode={inputMode}
        maxLength={maxLength}
        aria-invalid={Boolean(error)}
      />
      {error ? <p className="field-error">{error}</p> : null}
    </label>
  );
}

function ReviewItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="review-item">
      <span>{label}</span>
      <strong>{value || "-"}</strong>
    </div>
  );
}
